import type { OpenedTrade } from "@/vybe-trader-client/opened-trades/types";
import { calculateTradeAccountPnl } from "@/vybe-trader-client/opened-trades/utils";
import type { Quote } from "@/vybe-trader-client/subscribed-symbols/types";
import type { Symbol as VybeSymbol } from "@/vybe-trader-client/symbols/types";
import {
  formatOrderType,
  VybeOrderState,
  VybeOrderType,
} from "@/vybe-trader-client/trade-history/types";
import type {
  TradeOverlayDirection,
  TradeOverlayItem,
  TradeOverlayLineKind,
  TradeOverlayOrderType,
  TradeOverlayQuote,
} from "./types";
import {
  defaultTradeOverlayTheme,
  type TradeOverlayTheme,
  withOpacity,
} from "./theme";

const PENDING_ORDER_TYPES = new Set<number>([
  VybeOrderType.BuyLimit,
  VybeOrderType.SellLimit,
  VybeOrderType.BuyStop,
  VybeOrderType.SellStop,
  VybeOrderType.BuyStopLimit,
  VybeOrderType.SellStopLimit,
]);

const ACTIVE_PENDING_STATES = new Set<number>([
  VybeOrderState.Started,
  VybeOrderState.Placed,
  VybeOrderState.Partial,
  VybeOrderState.RequestAdding,
  VybeOrderState.RequestModifying,
]);

export interface TradeOverlayPnlContext {
  accountCurrency?: string | null;
  symbols?: Map<string, VybeSymbol>;
  quotes?: Map<string, Quote | { quote?: Quote } | undefined>;
}

/**
 * Returns the projected account-currency P&L if the given trade closed at
 * `targetPrice`. Works for both market trades and pending orders by treating
 * the pending order as a market trade of the same direction. The result is
 * the same value `normalizeTradeOverlayItem` precomputes for the static TP/SL
 * displays, but exposed as a helper so drag handlers can reproject on every
 * intermediate price during a drag.
 */
export function projectTradePnlAtPrice(
  trade: OpenedTrade,
  targetPrice: number,
  direction: TradeOverlayDirection,
  pnlContext?: TradeOverlayPnlContext
): number {
  if (!targetPrice || !Number.isFinite(targetPrice) || targetPrice <= 0) {
    return 0;
  }

  const synthetic: OpenedTrade = {
    ...trade,
    orderType:
      direction === "BUY" ? VybeOrderType.Buy : VybeOrderType.Sell,
    closePrice: targetPrice,
  };

  return calculateTradeAccountPnl({
    trade: synthetic,
    symbol: pnlContext?.symbols?.get(trade.symbol),
    quote: undefined,
    accountCurrency: pnlContext?.accountCurrency,
    symbols: pnlContext?.symbols,
    quotes: pnlContext?.quotes,
  });
}

export function normalizeTradeOverlayItems(
  trades: OpenedTrade[],
  selectedSymbol?: string | null,
  quote?: TradeOverlayQuote | null,
  pnlContext?: TradeOverlayPnlContext
): TradeOverlayItem[] {
  return trades
    .filter((trade) => symbolMatches(trade.symbol, selectedSymbol))
    .map((trade) => normalizeTradeOverlayItem(trade, quote, pnlContext))
    .filter((item): item is TradeOverlayItem => item !== null);
}

export function normalizeTradeOverlayItem(
  trade: OpenedTrade,
  quote?: TradeOverlayQuote | null,
  pnlContext?: TradeOverlayPnlContext
): TradeOverlayItem | null {
  const isPending = isPendingTrade(trade);
  const isMarket = isMarketTrade(trade);

  if (!isPending && !isMarket) {
    return null;
  }

  const direction = getDirection(trade.orderType);
  if (!direction || !Number.isFinite(trade.openPrice) || trade.openPrice <= 0) {
    return null;
  }

  const digits = trade.digits || 5;

  const tp = trade.takeProfit && trade.takeProfit > 0 ? trade.takeProfit : null;
  const sl = trade.stopLoss && trade.stopLoss > 0 ? trade.stopLoss : null;

  return {
    ticket: trade.ticket,
    symbol: trade.symbol,
    direction,
    orderType: getOverlayOrderType(trade.orderType),
    orderTypeLabel: formatOrderType(trade.orderType).toUpperCase(),
    isPending,
    lots: trade.lots || 0,
    openPrice: trade.openPrice,
    tp,
    sl,
    floatingPnl: isPending
      ? 0
      : calculateTradeAccountPnl({
          trade,
          symbol: pnlContext?.symbols?.get(trade.symbol),
          quote: quote as Quote | undefined,
          accountCurrency: pnlContext?.accountCurrency,
          symbols: pnlContext?.symbols,
          quotes: pnlContext?.quotes,
        }),
    tpProjectedPnl: tp
      ? projectTradePnlAtPrice(trade, tp, direction, pnlContext)
      : 0,
    slProjectedPnl: sl
      ? projectTradePnlAtPrice(trade, sl, direction, pnlContext)
      : 0,
    openTime: getTradeOpenTime(trade),
    expiry: getTradeExpiry(trade),
    comment: trade.comment,
    magic: trade.expertId,
    digits,
    contractSize: trade.contractSize || 100000,
    profitRate: trade.profitRate || 1,
    accountCurrency: pnlContext?.accountCurrency,
    source: trade,
  };
}

export function isMarketTrade(trade: OpenedTrade): boolean {
  return (
    (trade.orderType === VybeOrderType.Buy ||
      trade.orderType === VybeOrderType.Sell) &&
    trade.lots > 0
  );
}

export function isPendingTrade(trade: OpenedTrade): boolean {
  return (
    PENDING_ORDER_TYPES.has(trade.orderType) &&
    ACTIVE_PENDING_STATES.has(trade.state)
  );
}

export function buildLineLabel(
  item: TradeOverlayItem,
  lineKind: TradeOverlayLineKind
): string {
  if (lineKind === "tp") {
    return `TP #${item.ticket} ${formatPrice(item.tp ?? item.openPrice, item.digits)}`;
  }

  if (lineKind === "sl") {
    return `SL #${item.ticket} ${formatPrice(item.sl ?? item.openPrice, item.digits)}`;
  }

  const sideIcon = getEntrySideIcon(item);
  const sideLabel = item.isPending ? item.orderTypeLabel : item.direction;

  return `${sideIcon} #${item.ticket} ${sideLabel} ${formatLots(item.lots)}`;
}

export function buildLineLeftStatus(
  item: TradeOverlayItem,
  lineKind: TradeOverlayLineKind,
  isPipsVisible: boolean,
  quote?: TradeOverlayQuote | null
): string | undefined {
  if (lineKind === "entry") {
    if (item.isPending) {
      return item.expiry ? `exp ${formatExpiry(item.expiry)}` : "exp -";
    }
    return isPipsVisible
      ? formatPips(calculateFloatingPips(item, quote))
      : formatMoney(item.floatingPnl, item.accountCurrency);
  }

  if (lineKind === "tp") {
    if (!item.tp) return undefined;
    return isPipsVisible
      ? formatPips(calculateTargetPips(item, item.tp))
      : formatMoney(item.tpProjectedPnl, item.accountCurrency);
  }

  if (lineKind === "sl") {
    if (!item.sl) return undefined;
    return isPipsVisible
      ? formatPips(calculateTargetPips(item, item.sl))
      : formatMoney(item.slProjectedPnl, item.accountCurrency);
  }

  return undefined;
}

/**
 * Recomputes the left-side P&L/pips text for a TP or SL line at an arbitrary
 * `price`, without going through `normalizeTradeOverlayItem` again. Used by
 * the overlay's `onPressedMoving` handler so the user sees the projected
 * profit/loss update live while they drag a line.
 *
 * Returns `undefined` for entry lines (their left status is the floating P&L
 * or expiry, neither of which is what the user is dragging) and for invalid
 * prices.
 */
export function computeLeftStatusForPrice(
  item: TradeOverlayItem,
  lineKind: TradeOverlayLineKind,
  price: number,
  isPipsVisible: boolean,
  pnlContext?: TradeOverlayPnlContext
): string | undefined {
  if (lineKind === "entry") return undefined;
  if (!Number.isFinite(price) || price <= 0) return undefined;

  if (isPipsVisible) {
    return formatPips(calculateTargetPips(item, price));
  }

  const projected = projectTradePnlAtPrice(
    item.source,
    price,
    item.direction,
    pnlContext
  );
  return formatMoney(projected, item.accountCurrency);
}

function getEntrySideIcon(item: TradeOverlayItem): string {
  if (item.isPending) {
    return "◇";
  }
  return item.direction === "BUY" ? "▲" : "▼";
}

export function getLineColor(
  item: TradeOverlayItem,
  lineKind: TradeOverlayLineKind,
  selectedTicket: string | null,
  theme: TradeOverlayTheme = defaultTradeOverlayTheme
): string {
  let color = theme.buyColor;

  if (lineKind === "tp") {
    color = theme.tpColor;
  } else if (lineKind === "sl") {
    color = theme.slColor;
  } else if (item.isPending) {
    color = theme.pendingColor;
  } else if (item.direction === "SELL") {
    color = theme.sellColor;
  }

  return shouldDim(item.ticket, selectedTicket)
    ? withOpacity(color, theme.dimmedOpacity)
    : color;
}

export function shouldDim(ticket: number, selectedTicket: string | null): boolean {
  return Boolean(selectedTicket && selectedTicket !== ticket.toString());
}

export function getLineWidth(
  ticket: number,
  selectedTicket: string | null
): number {
  return selectedTicket === ticket.toString() ? 2 : 1;
}

export function formatPrice(price: number, digits: number): string {
  return price.toFixed(Math.max(0, Math.min(digits || 5, 8)));
}

function calculateFloatingPips(
  item: TradeOverlayItem,
  quote?: TradeOverlayQuote | null
): number {
  if (!quote?.bid || !quote?.ask) {
    return 0;
  }

  const currentPrice = item.direction === "BUY" ? quote.bid : quote.ask;
  const diff =
    item.direction === "BUY"
      ? currentPrice - item.openPrice
      : item.openPrice - currentPrice;
  const pipValue = Math.pow(10, -Math.max(item.digits - 1, 0));

  return pipValue > 0 ? diff / pipValue : 0;
}

export function calculateTargetPips(
  item: TradeOverlayItem,
  targetPrice: number
): number {
  if (!Number.isFinite(targetPrice) || targetPrice <= 0) {
    return 0;
  }

  const diff =
    item.direction === "BUY"
      ? targetPrice - item.openPrice
      : item.openPrice - targetPrice;
  const pipValue = Math.pow(10, -Math.max(item.digits - 1, 0));

  return pipValue > 0 ? diff / pipValue : 0;
}

function getDirection(orderType: number): TradeOverlayDirection | null {
  switch (orderType) {
    case VybeOrderType.Buy:
    case VybeOrderType.BuyLimit:
    case VybeOrderType.BuyStop:
    case VybeOrderType.BuyStopLimit:
      return "BUY";
    case VybeOrderType.Sell:
    case VybeOrderType.SellLimit:
    case VybeOrderType.SellStop:
    case VybeOrderType.SellStopLimit:
      return "SELL";
    default:
      return null;
  }
}

function getOverlayOrderType(orderType: number): TradeOverlayOrderType {
  switch (orderType) {
    case VybeOrderType.BuyLimit:
      return "BUY_LIMIT";
    case VybeOrderType.SellLimit:
      return "SELL_LIMIT";
    case VybeOrderType.BuyStop:
      return "BUY_STOP";
    case VybeOrderType.SellStop:
      return "SELL_STOP";
    case VybeOrderType.BuyStopLimit:
      return "BUY_STOP_LIMIT";
    case VybeOrderType.SellStopLimit:
      return "SELL_STOP_LIMIT";
    default:
      return "MARKET";
  }
}

function symbolMatches(tradeSymbol: string, selectedSymbol?: string | null): boolean {
  if (!selectedSymbol) return false;
  return tradeSymbol === selectedSymbol || tradeSymbol === `${selectedSymbol}.`;
}

export function getTradeOpenTime(trade: OpenedTrade): number {
  if (trade.openTimestampUTC) {
    return normalizeTimestamp(trade.openTimestampUTC);
  }

  return parseDateTime(trade.openTime) || Date.now();
}

function getTradeExpiry(trade: OpenedTrade): number | undefined {
  const parsed = parseDateTime(trade.expirationTime);
  return parsed || undefined;
}

function parseDateTime(value?: string | null): number {
  if (!value || value.startsWith("0001-01-01")) return 0;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function normalizeTimestamp(timestamp: number): number {
  return timestamp > 0 && timestamp < 1_000_000_000_000
    ? timestamp * 1000
    : timestamp;
}

function formatLots(lots: number): string {
  return lots.toFixed(2);
}

function formatMoney(value: number, currency?: string | null): string {
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(2)}${currency ? ` ${currency}` : ""}`;
}

function formatPips(value: number): string {
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}p`;
}

function formatExpiry(timestamp: number): string {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}`;
}
